import { useQuery } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import LoadingIndicator from 'primitives/LoadingIndicator';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

const ADD_DEAL_ENGAGEMENT_RULES_MUTATION = gql(`
  mutation AddDealEngagementRules($dealId: ID!, $customEngagementRules: [String!]!) {
    addDealEngagementRules(dealId: $dealId, customEngagementRules: $customEngagementRules) {
      id
      customEngagementRules
    }
  }
`);

const GET_DEAL_ENGAGEMENT_RULES_QUERY = gql(`
  query GetDealEngagementRules($dealId: ID!) {
    deal(id: $dealId) {
      id
      customEngagementRules
    }
  }
`);

const DISCLOSURES_QUERY = gql(`
  query DealDisclosures($id: ID!) {
    deal(id: $id) {
      id
      dealDisclosures {
        id
        risksNote
        hasLeadInvestedInCompanyBefore
        doesLeadHaveAdvisoryShares
        isLeadEmployeeAtCompany
        doesLeadHaveRelativesInCompany
        hasSameTermsForCoInvestors
        isLeadInvestingOnSameTerms
        isLeadInvestingFromFund
        otherDisclosuresNote
      }
    }
  }
`);

const UPSERT_DEAL_DISCLOSURES_MUTATION = gql(`
  mutation UpsertDealDisclosures(
    $dealId: ID!
    $risksNote: String
    $hasLeadInvestedInCompanyBefore: Boolean!
    $doesLeadHaveAdvisoryShares: Boolean!
    $isLeadEmployeeAtCompany: Boolean!
    $doesLeadHaveRelativesInCompany: Boolean!
    $hasSameTermsForCoInvestors: Boolean!
    $isLeadInvestingOnSameTerms: Boolean!
    $isLeadInvestingFromFund: Boolean!
    $otherDisclosuresNote: String
  ) {
    upsertDealDisclosures(
      dealId: $dealId
      risksNote: $risksNote
      hasLeadInvestedInCompanyBefore: $hasLeadInvestedInCompanyBefore
      doesLeadHaveAdvisoryShares: $doesLeadHaveAdvisoryShares
      isLeadEmployeeAtCompany: $isLeadEmployeeAtCompany
      doesLeadHaveRelativesInCompany: $doesLeadHaveRelativesInCompany
      hasSameTermsForCoInvestors: $hasSameTermsForCoInvestors
      isLeadInvestingOnSameTerms: $isLeadInvestingOnSameTerms
      isLeadInvestingFromFund: $isLeadInvestingFromFund
      otherDisclosuresNote: $otherDisclosuresNote
    ) {
      id
      dealDisclosures {
        id
        risksNote
        hasLeadInvestedInCompanyBefore
        doesLeadHaveAdvisoryShares
        isLeadEmployeeAtCompany
        doesLeadHaveRelativesInCompany
        hasSameTermsForCoInvestors
        isLeadInvestingOnSameTerms
        isLeadInvestingFromFund
        otherDisclosuresNote
      }
    }
  }
`);

const DealRisksDisclosures: FC = () => {
  const { dealId } = useParams<{ dealId: string }>() as { dealId: string };

  const { loading, error, data, refetch } = useQuery(DISCLOSURES_QUERY, {
    variables: {
      id: dealId,
    },
  });

  const {
    loading: engagementLoading,
    data: engagementRulesData,
    refetch: refetchEngagementRules,
  } = useQuery(GET_DEAL_ENGAGEMENT_RULES_QUERY, {
    variables: { dealId },
    skip: !dealId,
  });

  const [upsertDealDisclosures, { loading: updateLoading, error: updateError }] = useMutation(
    UPSERT_DEAL_DISCLOSURES_MUTATION
  );

  const [addDealEngagementRules, { loading: addEngagementLoading, error: addEngagementError }] =
    useMutation(ADD_DEAL_ENGAGEMENT_RULES_MUTATION);

  if (loading) return <LoadingIndicator />;

  if (error || !data || !dealId) return <ErrorMessage error={error} refetch={refetch} />;

  const defaultEngagementRules = (engagementRulesData?.deal?.customEngagementRules ?? []).filter(
    (r): r is string => r != null && r !== ''
  );
  const defaultEnableCustomEngagementRules = defaultEngagementRules.length > 0;

  return (
    <div>
      <FormPanelWithReadMode
        loading={updateLoading}
        error={updateError}
        onSubmit={formData => {
          Promise.all([
            addDealEngagementRules({
              variables: {
                dealId,
                customEngagementRules: formData?.enableCustomEngagementRules
                  ? formData.customEngagementRules
                  : [],
              },
            }),
            upsertDealDisclosures({
              variables: {
                dealId,
                risksNote: formData.risksNote,
                hasLeadInvestedInCompanyBefore: formData.hasLeadInvestedInCompanyBefore,
                doesLeadHaveAdvisoryShares: formData.doesLeadHaveAdvisoryShares,
                isLeadEmployeeAtCompany: formData.isLeadEmployeeAtCompany,
                doesLeadHaveRelativesInCompany: formData.doesLeadHaveRelativesInCompany,
                hasSameTermsForCoInvestors: formData.hasSameTermsForCoInvestors,
                isLeadInvestingOnSameTerms: formData.isLeadInvestingOnSameTerms,
                isLeadInvestingFromFund: formData.isLeadInvestingFromFund,
                otherDisclosuresNote: formData.otherPotentialConflicts
                  ? formData.otherDisclosures
                  : undefined,
              },
            }),
          ]).then(() => {
            refetch();
            refetchEngagementRules();
          });
        }}
        title="Risks and Disclosures"
      >
        <FormInput
          fieldName="risksNote"
          type="textarea"
          defaultValue={data.deal.dealDisclosures?.risksNote || 'Risks and Disclosures'}
          helperText="Disclosing specific risk factors is required for compliance"
          label=""
        />
        <FormInput
          type="switch"
          fieldName="hasLeadInvestedInCompanyBefore"
          label="I invested in a previous round"
          defaultValue={data.deal.dealDisclosures?.hasLeadInvestedInCompanyBefore || false}
        />
        <FormInput
          type="switch"
          fieldName="doesLeadHaveAdvisoryShares"
          label="I have advisory shares, warrants, or other shares"
          defaultValue={data.deal.dealDisclosures?.doesLeadHaveAdvisoryShares || false}
        />
        <FormInput
          type="switch"
          fieldName="isLeadEmployeeAtCompany"
          label="I am an officer or employee of the company"
          defaultValue={data.deal.dealDisclosures?.isLeadEmployeeAtCompany || false}
        />
        <FormInput
          type="switch"
          fieldName="doesLeadHaveRelativesInCompany"
          label="I have a relative or significant other working at the company"
          defaultValue={data.deal.dealDisclosures?.doesLeadHaveRelativesInCompany || false}
        />
        <FormInput
          type="switch"
          fieldName="hasSameTermsForCoInvestors"
          label="I have the same terms as other co-investors"
          defaultValue={data.deal.dealDisclosures?.hasSameTermsForCoInvestors || false}
        />
        <FormInput
          type="switch"
          fieldName="isLeadInvestingOnSameTerms"
          label="I am investing on the same terms as other investors"
          defaultValue={data.deal.dealDisclosures?.isLeadInvestingOnSameTerms || false}
        />
        <FormInput
          type="switch"
          fieldName="isLeadInvestingFromFund"
          label="I am investing from a fund"
          defaultValue={data.deal.dealDisclosures?.isLeadInvestingFromFund || false}
        />
        <FormInput
          type="switch"
          fieldName="otherPotentialConflicts"
          label="I have other potential conflicts to disclose"
          defaultValue={data.deal.dealDisclosures?.otherDisclosuresNote ? true : false}
        />
        <FormInput
          type="textarea"
          fieldName="otherDisclosures"
          label="Other Disclosures"
          defaultValue={data.deal.dealDisclosures?.otherDisclosuresNote || ''}
          conditionsToShow={{
            matches: [
              {
                field: 'otherPotentialConflicts',
                condition: '===',
                value: 'true',
              },
            ],
          }}
        />
        <div className="col-span-full mt-4">
          <div className="mt-4 space-y-4 rounded-lg border border-gray-200 bg-white p-4">
            <FormInput
              type="switch"
              fieldName="enableCustomEngagementRules"
              label="Disclose deal-specific restrictions (if any)"
              helperText="Select conditions to determine how this investment opportunity is accessed"
              defaultValue={defaultEnableCustomEngagementRules}
            />
            <FormInput
              type="filter-multi-select"
              fieldName="customEngagementRules"
              label="Select condition"
              placeholder="Select"
              defaultValue={defaultEngagementRules}
              options={[
                {
                  value:
                    'I/We am/are not presently employed at or have an existing stake in a direct or closely competing business',
                  label:
                    'Investors must not be currently employed at or having an existing stake in a direct or closely competing business',
                },
                {
                  value:
                    'I/We confirm that we have previously invested in or have worked in/with companies in the same sector',
                  label:
                    'Limit participation to investors with prior or current sector-specific/domain experience or exposure',
                },
              ]}
              conditionsToShow={{
                matches: [
                  {
                    field: 'enableCustomEngagementRules',
                    condition: '===',
                    value: 'true',
                  },
                ],
              }}
              onOthersPick={() => {}}
            />
          </div>
        </div>
      </FormPanelWithReadMode>
    </div>
  );
};

export default DealRisksDisclosures;
